import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { database, auth } from './firebase';
import { ref, onValue, push, set, remove } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { uploadImage } from './cloudinary';
import { FaTrash, FaEdit } from 'react-icons/fa';
import AdminNavbar from './components/AdminNavbar';

const Container = styled.div`
  max-width: 1300px;
  margin: 5rem auto 1rem auto;
  padding: 1rem;
`;

const Title = styled.h2`
  font-size: 2rem;
  margin-bottom: 2rem;
  color: #333;
  margin-left: 1rem;
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormWrapper = styled.div`
  background: white;
  padding: 0rem;
  border-radius: 15px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  height: fit-content;
  position: relative;
  top: -5rem;
  max-width: 600px;

  @media (max-width: 768px) {
    top: 0rem;
  }
`;


const FormHeading = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #333;
  text-align: center;
  font-weight: 400;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  min-height: 100px;
`;

const Button = styled.button`
  padding: 0.75rem;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    background: #0056b3;
  }
`;

// const DeleteButton = styled(Button)`
//   background: #dc3545;
//   &:hover {
//     background: #c82333;
//   }
// `;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  max-width: 1000px;
  align-items: start;
  height: fit-content;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TeamCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.05);
  min-width: 300px;
  min-height: 300px;
  justify-content: space-between;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
    
    &::before {
      opacity: 1;
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #007bff, #00bcd4);
    opacity: 0.7;
    transition: opacity 0.3s ease;
  }
`;

const TeamCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const TeamImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #f0f0f0;
  transition: transform 0.3s ease;

  ${TeamCard}:hover & {
    transform: scale(1.05);
  }
`;

const TeamInfo = styled.div`
  flex: 1;
`;

const TeamName = styled.h3`
  font-size: 1.2rem;
  margin: 0;
  color: #333;
  font-weight: 600;
`;

const TeamRole = styled.p`
  color: #666;
  margin: 0.25rem 0 0;
  font-size: 0.9rem;
`;

const TeamDescription = styled.p`
  color: #555;
  line-height: 1.5;
  margin: 0;
  font-size: 0.9rem;
`;

const TeamSkills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const SkillTag = styled.span`
  background: #f0f0f0;
  color: #666;
  padding: 0.3rem 0.6rem;
  border-radius: 15px;
  font-size: 0.8rem;
  transition: all 0.3s ease;

  ${TeamCard}:hover & {
    background: #e3f2fd;
    color: #007bff;
  }
`;

const TeamActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0.4rem;
  border-radius: 5px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.9rem;

  &:hover {
    background: #f0f0f0;
    color: #007bff;
  }

  svg {
    font-size: 1rem;
  }
`;

const FileInput = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  width: 100%;
`;

const FormLabel = styled.label`
  font-size: 0.9rem;
  color: #555;
  margin-bottom: 0.25rem;
  display: block;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  padding: 0.75rem;
  border-radius: 5px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  color: #28a745;
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  padding: 0.75rem;
  border-radius: 5px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const TeamForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: white;
  padding: 1.5rem 2.3rem;
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0,0,0,0.1);

  @media (max-width: 768px) {
    padding: 1rem 1rem;
  };
`;

const TeamMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  // const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    description: '',
    email: '',
    phone: '',
    linkedin: '',
    github: '',
    skills: '',
    image: null
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const membersRef = ref(database, 'teamMembers');
    onValue(membersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const membersList = Object.entries(data).map(([id, member]) => ({
          id,
          ...member
        }));
        setMembers(membersList);
      }
    });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFormData(prev => ({
        ...prev,
        image: e.target.files[0]
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.image) {
      setError('Please select an image');
      return;
    }

    setUploading(true);
    try {
      console.log('Starting team member creation process...');

      // Upload image to ImgBB
      console.log('Uploading image...');
      const imageUrl = await uploadImage(formData.image);
      console.log('Image uploaded successfully, URL:', imageUrl);

      // Save member data with image URL
      console.log('Saving team member data...');
      const membersRef = ref(database, 'teamMembers');
      const newMemberRef = push(membersRef);
      await set(newMemberRef, {
        name: formData.name,
        role: formData.role,
        description: formData.description,
        email: formData.email,
        phone: formData.phone,
        linkedin: formData.linkedin,
        github: formData.github,
        skills: formData.skills.split(',').map(skill => skill.trim()),
        imageUrl: imageUrl,
        createdAt: new Date().toISOString()
      });
      console.log('Team member saved successfully');

      setSuccess('Team member added successfully!');
      setFormData({
        name: '',
        role: '',
        description: '',
        email: '',
        phone: '',
        linkedin: '',
        github: '',
        skills: '',
        image: null
      });
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setError('Error adding team member: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const memberRef = ref(database, `teamMembers/${id}`);
      await remove(memberRef);
    } catch (error) {
      console.error('Error deleting team member:', error);
    }
  };

  // const handleLogout = async () => {
  //   try {
  //     await signOut(auth);
  //     navigate('/admin');
  //   } catch (error) {
  //     console.error('Error logging out:', error);
  //   }
  // };

  return (
    <Container>
      <AdminNavbar />
      <Title>Manage Members</Title>
      <ContentWrapper>
        <TeamGrid>
          {members.map(member => (
            <TeamCard key={member.id}>
              <TeamCardHeader>
                <TeamImage
                  src={member.imageUrl || 'https://via.placeholder.com/80'}
                  alt={member.name}
                />
                <TeamInfo>
                  <TeamName>{member.name}</TeamName>
                  <TeamRole>{member.role}</TeamRole>
                </TeamInfo>
              </TeamCardHeader>
              <TeamDescription>{member.description}</TeamDescription>
              <TeamSkills>
                {member.skills?.map((skill, index) => (
                  <SkillTag key={index}>{skill}</SkillTag>
                ))}
              </TeamSkills>
              <TeamActions>
                <ActionButton onClick={() => setEditingMember(member)}>
                  <FaEdit /> Edit
                </ActionButton>
                <ActionButton onClick={() => handleDelete(member.id)}>
                  <FaTrash /> Delete
                </ActionButton>
              </TeamActions>
            </TeamCard>
          ))}
        </TeamGrid>
        <FormWrapper>
          <TeamForm onSubmit={handleSubmit}>
            <FormHeading>Add New Member</FormHeading>
            <FormGroup>
              <FormLabel htmlFor="name">Name</FormLabel>
              <Input
                id="name"
                type="text"
                name="name"
                placeholder="Enter member name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="role">Role</FormLabel>
              <Input
                id="role"
                type="text"
                name="role"
                placeholder="Enter member role"
                value={formData.role}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="email">Email</FormLabel>
              <Input
                id="email"
                type="email"
                name="email"
                placeholder="Enter member email"
                value={formData.email}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="phone">Phone</FormLabel>
              <Input
                id="phone"
                type="tel"
                name="phone"
                placeholder="Enter member phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="linkedin">LinkedIn URL</FormLabel>
              <Input
                id="linkedin"
                type="url"
                name="linkedin"
                placeholder="Enter member LinkedIn URL"
                value={formData.linkedin}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="github">GitHub URL</FormLabel>
              <Input
                id="github"
                type="url"
                name="github"
                placeholder="Enter member GitHub URL"
                value={formData.github}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="description">Description</FormLabel>
              <TextArea
                id="description"
                name="description"
                placeholder="Enter member description"
                value={formData.description}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="skills">Skills</FormLabel>
              <Input
                id="skills"
                type="text"
                name="skills"
                placeholder="Enter skills (comma separated)"
                value={formData.skills}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="image">Profile Image</FormLabel>
              <FileInput
                id="image"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
              />
            </FormGroup>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}
            <Button type="submit" disabled={uploading}>
              {uploading ? 'Uploading...' : 'Add Member'}
            </Button>
          </TeamForm>
        </FormWrapper>

      </ContentWrapper>
    </Container>
  );
};

export default TeamMembers; 